import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, Skeleton, Typography } from '@mui/material'

import { TagGroup } from '../../../../../api/core'
import { CountChip } from '../../../../../features/market-analytics/components/CountChip'
import { FILTER_CHIP_HEIGHT, FILTER_ROW_LABEL_WIDTH } from '../../../../../features/market-analytics/components/helpers/filterChipStyles'
import languageService from '../../../../../services/LanguageService'
import { BlueprintFamilyUsage } from '../../hooks/useBlueprintFamilyUsage'

// Sentinel id used both as a filter value and as the grouping key for blueprints
// without a customer-facing event category.
export const UNCATEGORIZED_FILTER_ID = 'uncategorized'

type EventFilterChipOption = {
  id: string
  name: string
  count: number
  colorHex?: string
}

type BlueprintFamiliesEventFilterBarProps = {
  // Overview rows filtered ONLY by the discontinued toggle (not by search or by
  // these event filters), so chip counts stay stable while the user filters.
  usages: BlueprintFamilyUsage[]
  eventCategories: TagGroup[]
  selectedEventCategoryIds: string[]
  selectedEventTypeIds: string[]
  onToggleEventCategory: (id: string) => void
  onToggleEventType: (id: string) => void
  onClearEventCategories: () => void
  onClearEventTypes: () => void
  onClearAll: () => void
  isLoading?: boolean
}

const ROW_LABEL_SX = {
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: 'text.secondary',
  fontWeight: 700,
  flexShrink: 0,
  width: FILTER_ROW_LABEL_WIDTH,
  minWidth: FILTER_ROW_LABEL_WIDTH,
  whiteSpace: 'nowrap' as const,
  // Match the chip height so the label lines up with the first chip row when the
  // chips wrap onto multiple lines (rows are aligned to flex-start).
  lineHeight: `${FILTER_CHIP_HEIGHT}px`,
}

// Outer row: fixed label column + chips column that wraps on its own, so every
// wrapped chip line starts under the first chip rather than under the label.
const ROW_SX = {
  display: 'flex',
  flexWrap: 'nowrap' as const,
  alignItems: 'flex-start',
  columnGap: 1,
}

const CHIPS_WRAP_SX = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  columnGap: 1,
  rowGap: 1,
  flex: '1 1 auto',
  minWidth: 0,
}

const SKELETON_WIDTHS = [120, 96, 108, 84, 132] as const

/**
 * Event category / event type filter chips for the Blueprints Overview list view.
 * Mirrors the Market Analytics CatalogueFilterBar interaction: multi-select
 * category chips (with per-category blueprint counts and the tag group color), a
 * cascading event type row that opens once at least one real category is picked,
 * and a clear-all footer. Rendered inside the existing filters CardContent so no
 * nested card surface is introduced.
 */
export const BlueprintFamiliesEventFilterBar: FC<BlueprintFamiliesEventFilterBarProps> = ({
  usages,
  eventCategories,
  selectedEventCategoryIds,
  selectedEventTypeIds,
  onToggleEventCategory,
  onToggleEventType,
  onClearEventCategories,
  onClearEventTypes,
  onClearAll,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  // Event categories present in the data, ordered by tag group order (the
  // eventCategories prop is already order-sorted), with unknown category ids
  // after them and the "Uncategorized" chip always last — the same ordering the
  // grouped sections below use.
  const categoryOptions = useMemo(() => {
    const counts: { [categoryId: string]: number } = {}
    usages.forEach((usage) => {
      const categoryId = usage.eventCategoryId || UNCATEGORIZED_FILTER_ID
      counts[categoryId] = (counts[categoryId] || 0) + 1
    })

    const knownCategoryIds = new Set(eventCategories.map((category) => category.id))
    const options: EventFilterChipOption[] = eventCategories
      .filter((category) => counts[category.id])
      .map((category) => ({ id: category.id, name: category.name, count: counts[category.id], colorHex: category.colorHex }))

    Object.keys(counts)
      .filter((categoryId) => categoryId !== UNCATEGORIZED_FILTER_ID && !knownCategoryIds.has(categoryId))
      .forEach((categoryId) => {
        options.push({ id: categoryId, name: languageService.getTranslation('tagGroups', categoryId) || categoryId, count: counts[categoryId] })
      })

    if (counts[UNCATEGORIZED_FILTER_ID]) {
      options.push({ id: UNCATEGORIZED_FILTER_ID, name: t('internal-live-events:uncategorized'), count: counts[UNCATEGORIZED_FILTER_ID] })
    }

    return options
  }, [usages, eventCategories, t])

  // The event type cascade only applies to real categories — "Uncategorized"
  // rows have no event type to narrow by.
  const selectedRealCategoryIds = useMemo(
    () => selectedEventCategoryIds.filter((categoryId) => categoryId !== UNCATEGORIZED_FILTER_ID),
    [selectedEventCategoryIds]
  )

  // Event types present among blueprints in the selected categories, tinted with
  // their category color, sorted by count (then name) like Market Analytics.
  const typeOptions = useMemo(() => {
    if (selectedRealCategoryIds.length === 0) return []

    const categorySet = new Set(selectedRealCategoryIds)
    const colorByCategoryId = new Map(eventCategories.map((category) => [category.id, category.colorHex]))
    const byType = new Map<string, { count: number; categoryId: string }>()

    usages.forEach((usage) => {
      if (!usage.eventTypeId || !usage.eventCategoryId || !categorySet.has(usage.eventCategoryId)) return
      const existing = byType.get(usage.eventTypeId)
      if (existing) existing.count += 1
      else byType.set(usage.eventTypeId, { count: 1, categoryId: usage.eventCategoryId })
    })

    return Array.from(byType.entries())
      .map(([id, { count, categoryId }]) => ({
        id,
        name: languageService.getTranslation('tags', id) || id,
        count,
        colorHex: colorByCategoryId.get(categoryId),
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
  }, [usages, eventCategories, selectedRealCategoryIds])

  const allCategoriesSelected = selectedEventCategoryIds.length === 0
  const allTypesSelected = selectedEventTypeIds.length === 0
  const activeFilterCount = selectedEventCategoryIds.length + selectedEventTypeIds.length

  return (
    <Box role="region" aria-label={t('internal-live-events:event_category')}>
      {/* Event category row */}
      <Box sx={ROW_SX} role="group" aria-label={t('internal-live-events:event_category')}>
        <Typography component="span" sx={ROW_LABEL_SX}>
          {t('internal-live-events:event_category')}
        </Typography>
        <Box sx={CHIPS_WRAP_SX}>
          {isLoading ? (
            SKELETON_WIDTHS.map((width, index) => (
              <Skeleton key={index} variant="rounded" width={width} height={FILTER_CHIP_HEIGHT} sx={{ borderRadius: `${FILTER_CHIP_HEIGHT / 2}px` }} />
            ))
          ) : (
            <>
              <CountChip label={t('internal-live-events:all_event_categories')} selected={allCategoriesSelected} onClick={onClearEventCategories} />
              {categoryOptions.map((option) => (
                <CountChip
                  key={option.id}
                  label={option.name}
                  count={option.count}
                  colorHex={option.colorHex}
                  selected={selectedEventCategoryIds.includes(option.id)}
                  value={option.id}
                  onSelect={onToggleEventCategory}
                />
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* Event type row — cascades open only once at least one real event
          category is picked. Dashed top border matches CatalogueFilterBar. */}
      {!isLoading && selectedRealCategoryIds.length > 0 && typeOptions.length > 0 ? (
        <Box
          sx={{ ...ROW_SX, mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}
          role="group"
          aria-label={t('internal-live-events:event_type')}
        >
          <Typography component="span" sx={ROW_LABEL_SX}>
            {t('internal-live-events:event_type')}
          </Typography>
          <Box sx={CHIPS_WRAP_SX}>
            <CountChip label={t('internal-live-events:all_event_types')} selected={allTypesSelected} onClick={onClearEventTypes} />
            {typeOptions.map((option) => (
              <CountChip
                key={option.id}
                label={option.name}
                count={option.count}
                colorHex={option.colorHex}
                selected={selectedEventTypeIds.includes(option.id)}
                value={option.id}
                onSelect={onToggleEventType}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      {/* Clear-all footer — only shown when at least one event filter is active. */}
      {!isLoading && activeFilterCount > 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: 1,
            mt: 1,
            pt: 1,
            borderTop: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('internal-live-events:event_filters_active', { count: activeFilterCount })}
          </Typography>
          <Button variant="text" size="small" onClick={onClearAll} sx={{ '@media (pointer: coarse)': { minHeight: 44 } }}>
            {t('internal-live-events:event_filters_clear_all')}
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}
